/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import kotlin.math.max

/**
 * TrendlineHelper.kt
 * Pure Kotlin implementation of linear regression for calculating trendlines on transaction spending data.
 * Supports all platforms (iOS, Android, Web) through Kotlin Multiplatform.
 */

/**
 * Represents a single point in the trendline with x, y coordinates and future projection flag.
 */
data class TrendPoint(
    val x: Int,
    val y: Double,
    val isFuture: Boolean
)

/**
 * Represents aggregated spending for a time period.
 */
data class PeriodSpending(
    val periodKey: String,
    val amount: Double
)

/**
 * Result of linear regression calculation.
 */
data class LinearRegressionResult(
    val slope: Double,      // m in y = mx + b
    val intercept: Double,  // b in y = mx + b
    val r2: Double          // coefficient of determination (goodness of fit)
)

/**
 * Helper object for calculating linear regression trendlines on transaction spending data.
 */
object TrendlineHelper {

    /**
     * Removes leading zeros from period spending data.
     * Keeps zeros that appear after the first non-zero value.
     *
     * @param data List of period spending amounts
     * @return Filtered list with leading zeros removed
     */
    fun filterLeadingZeros(data: List<Double>): List<Double> {
        val firstNonZeroIndex = data.indexOfFirst { it > 0.0 }

        // If all zeros, return empty list
        if (firstNonZeroIndex == -1) {
            return emptyList()
        }

        return data.drop(firstNonZeroIndex)
    }

    /**
     * Returns adaptive forecast horizon based on time interval.
     *
     * @param timeInterval One of: "Weekly", "Monthly", "Quarterly", "Yearly"
     * @return Number of periods to project forward
     */
    fun getProjectionLength(timeInterval: String): Int {
        return when (timeInterval) {
            "Weekly" -> 8      // 8 weeks ahead
            "Monthly" -> 4     // 4 months ahead
            "Quarterly" -> 2   // 2 quarters ahead
            "Yearly" -> 1      // 1 year ahead
            else -> 4          // Default to 4 periods
        }
    }

    /**
     * Checks if data has sufficient points for trendline calculation.
     *
     * @param data List of period spending amounts
     * @return True if data has >= 3 non-zero points after filtering leading zeros
     */
    fun hasSufficientData(data: List<Double>): Boolean {
        val cleanData = filterLeadingZeros(data)
        return cleanData.size >= 3
    }

    /**
     * Calculates linear regression on data points.
     * Uses least-squares method to find best-fit line.
     *
     * @param data List of (x, y) pairs
     * @return LinearRegressionResult with slope, intercept, and R²
     */
    fun calculateLinearRegression(data: List<Pair<Int, Double>>): LinearRegressionResult {
        val n = data.size

        // Calculate means
        val xMean = data.map { it.first.toDouble() }.average()
        val yMean = data.map { it.second }.average()

        // Calculate slope: m = Σ((x - x̄)(y - ȳ)) / Σ((x - x̄)²)
        var numerator = 0.0
        var denominator = 0.0

        for ((x, y) in data) {
            val xDiff = x - xMean
            val yDiff = y - yMean
            numerator += xDiff * yDiff
            denominator += xDiff * xDiff
        }

        val slope = if (denominator != 0.0) numerator / denominator else 0.0

        // Calculate intercept: b = ȳ - m * x̄
        val intercept = yMean - slope * xMean

        // Calculate R² (coefficient of determination)
        val yPredictions = data.map { (x, _) -> slope * x + intercept }
        val ssRes = data.zip(yPredictions).sumOf { (point, pred) ->
            val residual = point.second - pred
            residual * residual
        }
        val ssTot = data.sumOf { (_, y) ->
            val diff = y - yMean
            diff * diff
        }

        val r2 = if (ssTot != 0.0) 1.0 - (ssRes / ssTot) else 0.0

        return LinearRegressionResult(slope, intercept, r2)
    }

    /**
     * Calculates trendline points with future projections.
     *
     * @param periodSpending List of period spending data
     * @param timeInterval Time interval ("Weekly", "Monthly", "Quarterly", "Yearly")
     * @return List of TrendPoint objects including historical and projected points
     */
    fun calculateTrendline(
        periodSpending: List<PeriodSpending>,
        timeInterval: String
    ): List<TrendPoint> {
        // Filter leading zeros
        val amounts = periodSpending.map { it.amount }
        val cleanData = filterLeadingZeros(amounts)

        // Check if we have sufficient data
        if (cleanData.size < 3) {
            return emptyList()
        }

        // Convert to (x, y) pairs for regression
        val regressionData = cleanData.mapIndexed { index, amount ->
            Pair(index, amount)
        }

        // Calculate regression
        val regression = calculateLinearRegression(regressionData)

        // Log R² for debugging
        println("Trendline R² = ${regression.r2}")

        // Calculate projection length
        val futurePoints = getProjectionLength(timeInterval)
        val totalPoints = cleanData.size + futurePoints

        // Generate trendline points (historical + projected)
        val trendline = mutableListOf<TrendPoint>()
        for (i in 0 until totalPoints) {
            val predictedY = regression.slope * i + regression.intercept

            // Prevent negative predictions
            val clampedY = max(0.0, predictedY)

            trendline.add(
                TrendPoint(
                    x = i,
                    y = clampedY,
                    isFuture = i >= cleanData.size
                )
            )
        }

        return trendline
    }

    /**
     * Splits trendline into historical and forecast datasets for Chart.js.
     * Ensures continuity by prepending last historical point to forecast.
     *
     * @param trendline Complete trendline with historical and projected points
     * @return Pair of (historicalTrend, forecastTrend)
     */
    fun splitTrendlineForChart(trendline: List<TrendPoint>): Pair<List<TrendPoint>, List<TrendPoint>> {
        val historical = trendline.filter { !it.isFuture }
        val forecast = trendline.filter { it.isFuture }.toMutableList()

        // Prepend last historical point to forecast for continuity
        if (historical.isNotEmpty() && forecast.isNotEmpty()) {
            forecast.add(0, historical.last())
        }

        return Pair(historical, forecast)
    }
}
